import { useState } from 'react';
import {
  type RegulationDraft,
  useEmployeeRegulation,
  useRegulationActions,
  useRoleGroups,
} from '../../api/regulations';
import { Icon } from '../../components/Icon';
import {
  cleanDraft,
  draftFrom,
  formatDate,
  isEmptyDraft,
  RegulationEditor,
  RegulationView,
} from '../../components/RegulationBlock';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';

/** Раздел «Регламент» кабинета: просмотр, подпись сотрудника, изменение руководителем или HR. */
export function RegulationSection({ employeeId }: { employeeId: string }) {
  const regulation = useEmployeeRegulation(employeeId, true);
  const { sign, save, changeGroup } = useRegulationActions(employeeId);
  const [draft, setDraft] = useState<RegulationDraft | null>(null);
  const [scope, setScope] = useState<'group' | 'personal'>('group');
  const [agreed, setAgreed] = useState(false);
  const groups = useRoleGroups(Boolean(regulation.data?.canChangeGroup));

  if (regulation.isPending) return <LoadingState label="Загружаю регламент" />;
  if (regulation.isError) {
    return <ErrorState error={regulation.error} onRetry={() => void regulation.refetch()} />;
  }

  const data = regulation.data;
  const current = data.regulation;
  const canEdit = data.canEdit.group || data.canEdit.personal;

  const startEdit = () => {
    setScope(data.personal || !data.canEdit.group ? 'personal' : 'group');
    setDraft(draftFrom(current));
  };

  return (
    <section id="regulation" className="panel regulation">
      <div className="panel-header">
        <h2>Регламент</h2>
        <div className="panel-actions">
          {draft ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setDraft(null)}>
                Отмена
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={save.isPending || isEmptyDraft(draft)}
                onClick={() =>
                  save.mutate({ scope, draft: cleanDraft(draft) }, { onSuccess: () => setDraft(null) })
                }
              >
                Сохранить
              </button>
            </>
          ) : (
            <>
              {current ? (
                <span className="reg-version">
                  Версия {current.version} от {formatDate(current.updatedAt)}
                  {data.personal ? ' · личный' : ''}
                </span>
              ) : null}
              {current && data.signedAt ? (
                <span className="status status-ok">
                  <Icon name="check" size={12} />
                  Подписан {formatDate(data.signedAt)}
                </span>
              ) : null}
              {current && !data.signedAt ? <span className="status status-bad">Не подписан</span> : null}
              {canEdit ? (
                <button type="button" className="btn-secondary" onClick={startEdit}>
                  <Icon name="pen" size={14} />
                  Изменить
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>

      {data.group ? (
        <div className="reg-group">
          <span className="muted">Группа должностей</span>
          {data.canChangeGroup && groups.data ? (
            <select
              className="select reg-group-select"
              value={data.group.id}
              aria-label="Группа должностей"
              disabled={changeGroup.isPending || Boolean(draft)}
              onChange={(event) => changeGroup.mutate(event.target.value)}
            >
              {groups.data.items.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          ) : (
            <strong>{data.group.name}</strong>
          )}
        </div>
      ) : null}

      {draft ? (
        <>
          {data.canEdit.group && data.canEdit.personal ? (
            <fieldset className="pill-row reg-scope">
              <legend className="visually-hidden">Для кого изменение</legend>
              <button
                type="button"
                className="pill-toggle"
                aria-pressed={scope === 'group'}
                onClick={() => setScope('group')}
              >
                Для всей группы «{data.group?.name}»
              </button>
              <button
                type="button"
                className="pill-toggle"
                aria-pressed={scope === 'personal'}
                onClick={() => setScope('personal')}
              >
                Только для этого сотрудника
              </button>
            </fieldset>
          ) : null}
          <RegulationEditor draft={draft} onChange={setDraft} />
          {save.isError ? <ErrorState title="Регламент не сохранён" error={save.error} /> : null}
        </>
      ) : null}

      {!draft && current ? (
        <>
          {data.canSign ? (
            <div className="sign-bar">
              <label className="sign-check">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(event) => setAgreed(event.target.checked)}
                />
                <span>Я ознакомлен(а) с регламентом, правилами и обязанностями</span>
              </label>
              <button
                type="button"
                className="btn-primary"
                disabled={!agreed || sign.isPending}
                onClick={() => sign.mutate(current.versionId)}
              >
                <Icon name="pen" size={14} />
                Подписать
              </button>
            </div>
          ) : null}
          {sign.isError ? (
            <ErrorState title="Не подписано" error={sign.error} onRetry={() => void regulation.refetch()} />
          ) : null}
          <RegulationView regulation={current} />
        </>
      ) : null}

      {!draft && !current ? (
        <EmptyState title="Регламент ещё не заполнен">
          {canEdit ? 'Нажмите «Изменить», чтобы добавить файлы, правила и обязанности.' : null}
        </EmptyState>
      ) : null}
    </section>
  );
}
